import { useEffect, useRef, useState } from 'react';
import useOutsideClick from 'hooks/useOutsideClick';
import styles from 'styles/modules/Dropdown.module.scss';
import Tick from 'components/Tick';
import { wordCount } from 'helpers/wordCount';
import { sanitise } from 'helpers/strings';

const ExpertiseDropdown = ({ data }) => {
    if (!data) return null;

    const isInline = !!data.inline;

    let { items } = data;

    if (data.exclude) {
        let exclude = data.exclude.map((item) => item.id);
        items = items.filter((item) => {
            return !exclude.includes(item.id);
        });
    }

    const [query, setQuery] = useState('');
    const [filteredList, setFilteredList] = useState([...data.items]);
    const [showList, setShowList] = useState(false);
    const inputRef = useRef(null);
    const [disabled, setDisabled] = useState(false);

    const handleSelect = (item) => {
        setShowList(false);
        setQuery('');
        data.callback(item);
    };

    const handleChange = (e) => {
        setQuery(e.target.value);
        if (e.target.value.length > 30 || wordCount(e.target.value) > 3) {
            setDisabled(true);
        } else {
            setDisabled(false);
        }
    };

    const handleKeyUp = () => {
        let filtered = [];

        if (query && query.trim() !== '') {
            if (!showList) setShowList(true);

            filtered = items.filter((item) => {
                return item.name.toLowerCase().includes(query.toLowerCase());
            });
        }
        setFilteredList(filtered);
    };

    const handleFocus = () => {
        if (query && query.trim() !== '') {
            if (!showList) setShowList(true);
        }
    };

    const handleKeyDown = (e) => {
        const sanitisedResult = sanitise(e.target.value.toLowerCase());
        const allowedToAdd =
            e.key === 'Enter' &&
            sanitisedResult.length > 2 &&
            sanitisedResult.length <= 30 &&
            !disabled;

        if (!allowedToAdd) {
            return;
        }

        e.preventDefault();
        data.callback({ name: sanitisedResult });
        setQuery('');
    };

    const handleSubmit = (value) => {
        if (value.length > 2) {
            data.callback(value);
            setQuery('');
        }
    };

    useEffect(() => {
        if ('query' in data) {
            setQuery(data.query);
        }
        if ('showList' in data) {
            setShowList(data.showList);
        }
    });

    useOutsideClick(inputRef, () => {
        if (showList) {
            setShowList(false);
        }
    });

    return (
        <>
            {disabled && (
                <p className={styles.error}>
                    Area of expertise should not have more than 3 words and 30
                    letters
                </p>
            )}
            <div
                className={[
                    styles.dropdown,
                    isInline ? styles.inline : null,
                ].join(' ')}
            >
                <div className={styles.input}>
                    <input
                        type="text"
                        value={query}
                        onChange={handleChange}
                        onKeyUp={handleKeyUp}
                        onFocus={handleFocus}
                        ref={inputRef}
                        placeholder={data.placeholder}
                    />
                </div>
                {showList && (
                    <div className={styles.listWrapper}>
                        <div className={styles.list}>
                            {filteredList.map((item, key) => {
                                return (
                                    <div
                                        key={`select-list-${key}`}
                                        onClick={() => handleSelect(item)}
                                        className={styles.listItem}
                                    >
                                        {item.name}

                                        {item.isMaster === 1 && (
                                            <span
                                                className={styles.masterTagBox}
                                            >
                                                All sub-sectors
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                {query.length > 2 && (
                    <Tick
                        onClick={() => handleSubmit(query)}
                        className={styles.submitBtn}
                        disabled={disabled}
                    />
                )}
            </div>
        </>
    );
};

export default ExpertiseDropdown;
